import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthUtils } from './auth.utils';
import { Router } from '@angular/router';

export interface User {
  id: number;
  email: string;
  teacher_profile: any;
  name_en: string;
  name_ar: string;
  profile_photo: string;
  is_active: boolean;
  is_verified: boolean;
  role: string;
  // add other properties your backend returns: name_en, profile_photo, etc.
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser$: Observable<User | null>;

  constructor(private http: HttpClient, private router: Router) {
    // Load user from localStorage if available
    const storedUser = localStorage.getItem('currentUser');
    this.currentUserSubject = new BehaviorSubject<User | null>(
      storedUser ? JSON.parse(storedUser) : null
    );
    this.currentUser$ = this.currentUserSubject.asObservable();
  }

  /**
   * Getter to simplify retrieving currentUser.
   */
  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  public signIn(
    credentials: { email: string; password: string,language: string }
  ): Observable<User> {
    return this.http.post<{
      message: string;
      accessToken: string;
      refreshToken: string;
      user: User;
    }>(
      `${environment.apiUrl}/auth/login`,
      credentials
    ).pipe(
      map((response) => {
        localStorage.setItem('accessToken', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        this.currentUserSubject.next(response.user);
        return response.user;
      }),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * 2) Refresh the short-lived access token using our long-lived refresh token.
   */
  public refreshToken(): Observable<string> {
    const refreshToken = localStorage.getItem('refreshToken');
  
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token found'));
    }

    return this.http.post<{ accessToken: string }>(
      `${environment.apiUrl}/auth/refresh`,
      { refreshToken }
    ).pipe(
      tap(response => {
        // Update the stored access token
        localStorage.setItem('accessToken', response.accessToken);
      }),
      map(response => response.accessToken),
      catchError((err) => {
        console.error('Refresh token request failed:', err);
        this.signOut(); // LOGOUT if refresh fails
        return throwError(() => err);
      })
    );
  }

  /**
   * Utility to get the current accessToken from localStorage.
   */
  public getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  /**
   * 3) Logout (remove tokens + user).
   * Optionally call the backend to invalidate refresh token in DB.
   */
  public signOut(): void {
    const user = this.currentUserValue;
  
    // 1. Clear local storage and navigate immediately
    console.log('Clearing local storage and navigating to login...');
    if (user) {
      localStorage.removeItem(`teacherSemesters_${user.id}`);
    }
    this.clearAuthStorage();
   // this.router.navigate(['/auth/login'], { replaceUrl: true });
  
    // 2. Send backend logout request (fire-and-forget)
    if (user) {
      this.http.post(`${environment.apiUrl}/auth/logout`, { userId: user.id }).subscribe({
        next: () => {
          console.log('Backend logout successful');
        },
        error: (err) => {
          console.error('Backend logout failed:', err);
        }
      });
    }
  }
  
  
  
  fetchLatestUser(): Observable<User> {
    return this.http.get<{ user: User }>(`${environment.apiUrl}/auth/me`).pipe(
      map(response => {
        this.updateUser(response.user);
        return response.user;
      }),
      catchError((err) => {
        console.error('Failed to fetch latest user data:', err);
        return throwError(() => err);
      })
    );
  }
  

  private clearAuthStorage(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');

    this.currentUserSubject.next(null);
  }

  /**
   * (Optional) Check if the user is currently logged in (simple version).
   * You could also verify by calling /users/me to check validity of the access token
   * if you want a stronger check.
   */
  public isLoggedIn(): boolean {
    return !!localStorage.getItem('accessToken');
  }


//-------------- Need more modification

get accessToken(): string {
  return localStorage.getItem('accessToken') || '';
}

set accessToken(token: string) {
  localStorage.setItem('accessToken', token);
}

verifyEmail(code: string): Observable<any> {
  return this.http.post(`${environment.apiUrl}/auth/verify-email`, { code });
}

resendVerificationCode(): Observable<any> {
  return this.http.post(`${environment.apiUrl}/auth/resend-verification-code`, {});
}
forgotPassword(email: string): Observable<any> {
  const language = localStorage.getItem('lang') || 'ar';
  return this.http.post(`${environment.apiUrl}/auth/forgot-password`, { email,language });
}

validateToken(token: string): Observable<any> {
  //pas lang as query
  const lang = localStorage.getItem('lang') || 'ar';
  return this.http.get(`${environment.apiUrl}/auth/validate-token/${token}`, {
    headers: {
      'Accept-Language': lang  // Pass the language in the request header
    }
  });
}

resetExistPassword(credentials: { password: string; newPassword: string }): Observable<any> {
  return this.http.put(`${environment.apiUrl}/auth/reset-password`, credentials);
}

updateUserData(newUserData: User): void {
  localStorage.removeItem('currentUser');
  localStorage.setItem('currentUser', JSON.stringify(newUserData));
  this.currentUserSubject.next(newUserData);
}

// ---------- Reset Forget Password ------------
resetPassword(token: string, newPassword: string): Observable<any> {
//pas lang as query
const lang = localStorage.getItem('lang') || 'ar';
return this.http.post(`${environment.apiUrl}/auth/reset-password`, { token, newPassword }, {
  headers: {
    'Accept-Language': lang  // Pass the language in the request header
  }
});
}


changePassword(passwordData: any): Observable<any> {
  return this.http.post(`${environment.apiUrl}/auth/reset-password`, passwordData);
}
check(): Observable<boolean> {
  const user = this.currentUserValue;
  if (user) {
    return of(true);
  }
  if (!this.accessToken || AuthUtils.isTokenExpired(this.accessToken)) {
    return of(false);
  }
  return this.signInUsingToken();
}


getUser(): User {
  return JSON.parse(localStorage.getItem('currentUser') || '{}');
}
getActiveSemestersForTeacher(): Observable<any> {
  const lang = localStorage.getItem('lang') || 'ar';
  return this.http.get(`${environment.apiUrl}/menu/active-semester`, {
    headers: {
      'Accept-Language': lang  // Pass the language in the request header
    }
  });
}

switchTeacherSemester(teacherId: number, semesterId: number): Observable<any> {
  return this.http.put(`${environment.apiUrl}/teacher/courses/switch-semester/switch`, { teacherId, semesterId });
}

signInUsingToken(): Observable<any> {
  return this.http.post(`${environment.apiUrl}/auth/sign-in-with-token`, { accessToken: this.accessToken }).pipe(
    switchMap((response: any) => {
      if (response.accessToken) {
        this.accessToken = response.accessToken;
      }
      this.updateUser(response.user);
      return of(true);
    }),
    catchError(() => of(false))
  );
}

updateUser(user: User): void {
  localStorage.setItem('currentUser', JSON.stringify(user));
  this.currentUserSubject.next(user);
  //this._userService.user = user;
}

checkEmailExistence(email: string): Observable<boolean> {
  return this.http.post<{ exists: boolean }>(`${environment.apiUrl}/auth/check-email-exist`, { email }).pipe(
    map(response => response.exists),
    catchError(() => of(false)) // Return false if there's an error
  );
}

public signUp(formData: any): Observable<User> {
  const lang = localStorage.getItem('lang') || 'ar';
  return this.http.post<{
    message: string;
    accessToken: string;
    refreshToken: string;
    user: User;
  }>(`${environment.apiUrl}/auth/register`, formData, {
    headers: {
      'Accept-Language': lang  // Pass the language in the request header
    }
  }).pipe(
    map((response) => {
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('currentUser', JSON.stringify(response.user));
      this.currentUserSubject.next(response.user);
        // Optional: Immediately fetch updated profile (especially for Teacher)
     this.fetchLatestUser().subscribe();
      return response.user;
    }),
    catchError((error) => throwError(() => error))
  );
}


}
